import React, { useEffect, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView, Modal, ActivityIndicator, Platform, ImageBackground, Linking, SafeAreaView } from 'react-native';
import AppTextInput from '../components/AppTextInput';
import Svg, { Path, Rect, Circle } from 'react-native-svg';
import BookAPTLogo from '../components/BookAPTLogo';
import { setGuestZipcodeAsync } from '../utils/guestSession';

const CORAL = '#e76f51';
const NAVY = '#1d3557';
const TEXT_MUTED = '#64748b';
const IS_WEB = Platform.OS === 'web';

const HERO_BG_SOURCE = IS_WEB
  ? { uri: '/hero-bg.jpg' }
  : require('../assets/images/hero-bg.jpg');

const TRUST_ICON_COLOR = '#ffffff';

const IconMobile = () => (
  <Svg width={18} height={18} viewBox="0 0 24 24" fill="none">
    <Rect x={6} y={2} width={12} height={20} rx={2.5} stroke={TRUST_ICON_COLOR} strokeWidth={1.75} />
    <Path d="M10 18h4" stroke={TRUST_ICON_COLOR} strokeWidth={1.75} strokeLinecap="round" />
  </Svg>
);

const IconMessage = () => (
  <Svg width={18} height={18} viewBox="0 0 24 24" fill="none">
    <Path
      d="M21 12a8 8 0 0 1-8 8H7l-4 3V12a8 8 0 1 1 16 0z"
      stroke={TRUST_ICON_COLOR}
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path d="M8 12h.01M12 12h.01M16 12h.01" stroke={TRUST_ICON_COLOR} strokeWidth={2} strokeLinecap="round" />
  </Svg>
);

const IconCard = () => (
  <Svg width={18} height={18} viewBox="0 0 24 24" fill="none">
    <Rect x={2} y={5} width={20} height={14} rx={2} stroke={TRUST_ICON_COLOR} strokeWidth={1.75} />
    <Path d="M2 10h20" stroke={TRUST_ICON_COLOR} strokeWidth={1.75} strokeLinecap="round" />
  </Svg>
);

const IconSearch = ({ color = NAVY }: { color?: string }) => (
  <Svg width={22} height={22} viewBox="0 0 24 24" fill="none">
    <Circle cx={11} cy={11} r={7} stroke={color} strokeWidth={1.75} />
    <Path d="M20 20l-3-3" stroke={color} strokeWidth={1.75} strokeLinecap="round" />
  </Svg>
);

const HERO_TRUST_ITEMS = [
  { id: 'mobile', label: 'iOS & Android', Icon: IconMobile },
  { id: 'sms', label: 'SMS Reminders', Icon: IconMessage },
  { id: 'payments', label: 'Stripe & PayPal', Icon: IconCard },
] as const;
/** Public marketing site; ?home=1 always shows the homepage (not repeat-visitor app redirect). */
const MARKETING_HOME_URL = 'https://bookapt.io/?home=1';
const SALES_REP_SIGNUP_URL = 'https://bookapt.io/sales-rep/signup';

export type GuestWelcomeDestination = 'explore' | 'book';

interface GuestWelcomeScreenProps {
  onExplore: () => void;
  onBook: () => void;
  onSignIn: () => void;
  onListBusiness?: () => void;
}

const GuestWelcomeScreen: React.FC<GuestWelcomeScreenProps> = ({
  onExplore,
  onBook,
  onSignIn,
  onListBusiness,
}) => {
  const [showZipModal, setShowZipModal] = useState(false);
  const [pendingDestination, setPendingDestination] = useState<GuestWelcomeDestination | null>(null);
  const [zipInput, setZipInput] = useState('');
  const [zipError, setZipError] = useState<string | null>(null);
  const [savingZip, setSavingZip] = useState(false);
  const [isWideWeb, setIsWideWeb] = useState(true);

  useEffect(() => {
    if (!IS_WEB || typeof window === 'undefined') return;
    const mq = window.matchMedia('(min-width: 900px)');
    const update = () => setIsWideWeb(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  const continueToDestination = (dest: GuestWelcomeDestination) => {
    if (dest === 'explore') {
      onExplore();
    } else {
      onBook();
    }
  };

  const handlePrimaryAction = (dest: GuestWelcomeDestination) => {
    setPendingDestination(dest);
    setZipInput('');
    setZipError(null);
    setShowZipModal(true);
  };

  const handleSaveZipAndContinue = async () => {
    const trimmed = zipInput.trim();
    if (!trimmed) {
      setZipError('Enter your ZIP or postal code');
      return;
    }
    const { geocodingService } = await import('../services/geocodingService');
    const normalized = geocodingService.normalizeZipcodeForStorage(trimmed);
    if (!normalized) {
      setZipError(
        'Enter a valid US ZIP (5 digits), Canadian postal code (e.g. A1A 1A1), or India PIN (6 digits).',
      );
      return;
    }

    setSavingZip(true);
    setZipError(null);
    try {
      await setGuestZipcodeAsync(normalized);
      setShowZipModal(false);
      const dest = pendingDestination;
      setPendingDestination(null);
      if (dest) {
        continueToDestination(dest);
      }
    } finally {
      setSavingZip(false);
    }
  };

  const openMarketingHome = () => {
    if (IS_WEB && typeof window !== 'undefined') {
      const origin = window.location.origin;
      const isLocalDev = /localhost|127\.0\.0\.1/.test(origin);
      window.location.assign(isLocalDev ? `${origin}/?home=1` : MARKETING_HOME_URL);
      return;
    }
    Linking.openURL(MARKETING_HOME_URL).catch(() => undefined);
  };

  // Sales rep signup/dashboard is a standalone web experience (not part of
  // this app's own screen stack), same reasoning as opening the marketing
  // site above — always opens externally, even on web, so mobile users get
  // this exactly like everyone else instead of only via a desktop browser.
  const openSalesRepSignup = () => {
    if (IS_WEB && typeof window !== 'undefined') {
      window.open(SALES_REP_SIGNUP_URL, '_blank', 'noopener,noreferrer');
      return;
    }
    Linking.openURL(SALES_REP_SIGNUP_URL).catch(() => undefined);
  };

  const isWideLayout = IS_WEB && isWideWeb;

  const zipModal = (
    <Modal visible={showZipModal} transparent animationType="slide" onRequestClose={() => setShowZipModal(false)}>
      <View style={styles.modalOverlay}>
        <View style={styles.modalCard}>
          <Text style={styles.modalTitle}>Where are you looking?</Text>
          <Text style={styles.modalSubtitle}>
            We use your ZIP or postal code to show businesses near you. You can update it later in Profile.
          </Text>
          <AppTextInput
            style={styles.zipInput}
            placeholder="e.g. 90210 or A1A 1A1"
            value={zipInput}
            onChangeText={(t) => {
              setZipInput(t);
              setZipError(null);
            }}
            autoCapitalize="characters"
            maxLength={7}
            autoComplete="postal-code"
            textContentType="postalCode"
          />
          {zipError ? <Text style={styles.zipError}>{zipError}</Text> : null}
          <TouchableOpacity
            style={styles.modalPrimary}
            onPress={handleSaveZipAndContinue}
            disabled={savingZip}
          >
            {savingZip ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <Text style={styles.modalPrimaryText}>Continue</Text>
            )}
          </TouchableOpacity>
          <TouchableOpacity style={styles.modalCancel} onPress={() => setShowZipModal(false)}>
            <Text style={styles.modalCancelText}>Cancel</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );

  const actionButtons = (
    <>
      <TouchableOpacity
        style={styles.bookButton}
        onPress={() => handlePrimaryAction('book')}
        activeOpacity={0.88}
      >
        <BookAPTLogo size="sm" showCardBackground style={styles.bookButtonLogo} />
        <View style={styles.buttonTextWrap}>
          <Text style={styles.bookButtonTitle}>Book an appointment</Text>
          <Text style={styles.bookButtonSub}>Choose category, business & service</Text>
        </View>
      </TouchableOpacity>

      <TouchableOpacity
        style={styles.exploreButton}
        onPress={() => handlePrimaryAction('explore')}
        activeOpacity={0.88}
      >
        <View style={styles.exploreIconWrap}>
          <IconSearch />
        </View>
        <View style={styles.buttonTextWrap}>
          <Text style={styles.exploreButtonTitle}>Explore nearby</Text>
          <Text style={styles.exploreButtonSub}>Browse salons, clinics, shops & more</Text>
        </View>
      </TouchableOpacity>

      <View style={styles.signInRow}>
        <Text style={styles.signInPrompt}>Already have an account?</Text>
        <TouchableOpacity onPress={onSignIn}>
          <Text style={styles.signInLink}>Sign in</Text>
        </TouchableOpacity>
      </View>

      {onListBusiness ? (
        <TouchableOpacity style={styles.businessLink} onPress={onListBusiness}>
          <Text style={styles.businessLinkText}>List your business on BookAPT</Text>
        </TouchableOpacity>
      ) : null}

      <TouchableOpacity style={styles.businessLink} onPress={openSalesRepSignup}>
        <Text style={styles.businessLinkText}>Become a BookAPT Rep — earn 5% commission</Text>
      </TouchableOpacity>
    </>
  );

  return (
    <ImageBackground source={HERO_BG_SOURCE} style={styles.root} imageStyle={styles.rootImage}>
      <View style={styles.pageOverlay}>
        <SafeAreaView style={styles.safeArea}>
          <View style={styles.header}>
            <View style={styles.headerInner}>
              <View style={styles.brandRow}>
                <BookAPTLogo size={40} showCardBackground />
                <Text style={styles.brandName}>BookAPT</Text>
              </View>
              <TouchableOpacity onPress={openMarketingHome} style={styles.aboutLink}>
                <Text style={styles.aboutLinkText}>{IS_WEB ? 'About BookAPT' : 'Learn more'}</Text>
              </TouchableOpacity>
            </View>
          </View>

          <ScrollView
            style={styles.scroll}
            contentContainerStyle={[
              styles.scrollContent,
              isWideLayout && styles.scrollContentWide,
            ]}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
          >
            <View style={[styles.heroCopy, isWideLayout && styles.heroCopyWide]}>
              <Text style={styles.heroEyebrow}>Smart scheduling platform</Text>
              <Text style={styles.heroTitle}>Book local services in minutes</Text>
              <Text style={styles.heroSub}>
                Salons, clinics, auto shops, and more — explore nearby businesses or jump straight into booking.
              </Text>
              <View style={styles.trustRow}>
                {HERO_TRUST_ITEMS.map(({ id, label, Icon }) => (
                  <View key={id} style={styles.trustItem}>
                    <View style={styles.trustIconWrap}>
                      <Icon />
                    </View>
                    <Text style={styles.trustLabel}>{label}</Text>
                  </View>
                ))}
              </View>
            </View>

            <View style={[styles.cardWrap, isWideLayout && styles.cardWrapWide]}>
              <View style={styles.actionCard}>
                <BookAPTLogo size="md" showCardBackground style={styles.cardLogo} />
                <Text style={styles.panelTitle}>Get started</Text>
                <Text style={styles.panelSub}>
                  No account required to browse. Sign in when you&apos;re ready to confirm.
                </Text>
                {actionButtons}
              </View>
            </View>
          </ScrollView>
        </SafeAreaView>
      </View>
      {zipModal}
    </ImageBackground>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: '#1b2838',
    ...(IS_WEB ? { minHeight: '100vh' as unknown as number, width: '100%' } : {}),
  },
  rootImage: {
    resizeMode: 'cover',
  },
  pageOverlay: {
    flex: 1,
    backgroundColor: 'rgba(20, 32, 52, 0.82)',
    ...(IS_WEB ? { minHeight: '100vh' as unknown as number } : {}),
  },
  safeArea: {
    flex: 1,
  },
  header: {
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.1)',
  },
  headerInner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: IS_WEB ? 28 : 20,
    paddingVertical: 12,
    maxWidth: 1140,
    width: '100%',
    alignSelf: 'center',
  },
  brandRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  brandName: {
    fontSize: 20,
    fontWeight: '700',
    color: '#ffffff',
    letterSpacing: -0.02,
  },
  aboutLink: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.35)',
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
  },
  aboutLinkText: {
    color: '#ffffff',
    fontSize: 13,
    fontWeight: '600',
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 32,
    gap: 24,
    maxWidth: 1140,
    width: '100%',
    alignSelf: 'center',
  },
  scrollContentWide: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 40,
    paddingVertical: 40,
    gap: 48,
    minHeight: 'calc(100vh - 80px)' as unknown as number,
  },
  heroCopy: {
    justifyContent: 'center',
  },
  heroCopyWide: {
    flex: 1,
    maxWidth: 520,
  },
  heroEyebrow: {
    fontSize: 12,
    fontWeight: '600',
    letterSpacing: 1.4,
    textTransform: 'uppercase',
    color: CORAL,
    marginBottom: 12,
  },
  heroTitle: {
    fontSize: IS_WEB ? 38 : 30,
    fontWeight: '800',
    color: '#ffffff',
    lineHeight: IS_WEB ? 44 : 36,
    letterSpacing: -0.03,
    marginBottom: 14,
  },
  heroSub: {
    fontSize: 16,
    color: 'rgba(255, 255, 255, 0.9)',
    lineHeight: 24,
    marginBottom: 20,
  },
  trustRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    rowGap: 10,
  },
  trustItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  trustIconWrap: {
    width: 32,
    height: 32,
    borderRadius: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.12)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  trustLabel: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.92)',
    fontWeight: '500',
  },
  cardWrap: {
    width: '100%',
  },
  cardWrapWide: {
    flexShrink: 0,
    width: 400,
    maxWidth: '100%',
  },
  actionCard: {
    backgroundColor: 'rgba(255, 255, 255, 0.97)',
    borderRadius: 20,
    paddingVertical: IS_WEB ? 36 : 28,
    paddingHorizontal: IS_WEB ? 28 : 22,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 16 },
    shadowOpacity: 0.28,
    shadowRadius: 32,
    elevation: 12,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.6)',
  },
  cardLogo: {
    alignSelf: 'center',
    marginBottom: 14,
  },
  panelTitle: {
    fontSize: 22,
    fontWeight: '800',
    color: NAVY,
    textAlign: 'center',
    marginBottom: 8,
    letterSpacing: -0.02,
  },
  panelSub: {
    fontSize: 15,
    color: TEXT_MUTED,
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: 24,
  },
  bookButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: CORAL,
    borderRadius: 14,
    padding: 16,
    marginBottom: 12,
    shadowColor: '#e76f51',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.35,
    shadowRadius: 12,
    elevation: 4,
  },
  bookButtonLogo: {
    marginRight: 14,
  },
  buttonTextWrap: {
    flex: 1,
  },
  bookButtonTitle: {
    fontSize: 17,
    fontWeight: '700',
    color: '#ffffff',
  },
  bookButtonSub: {
    fontSize: 13,
    color: 'rgba(255, 255, 255, 0.92)',
    marginTop: 3,
  },
  exploreButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f0f4f8',
    borderRadius: 14,
    padding: 16,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: 'rgba(29, 53, 87, 0.12)',
  },
  exploreIconWrap: {
    width: 48,
    height: 48,
    borderRadius: 12,
    backgroundColor: '#ffffff',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 14,
    borderWidth: 1,
    borderColor: 'rgba(29, 53, 87, 0.08)',
  },
  exploreButtonTitle: {
    fontSize: 17,
    fontWeight: '700',
    color: NAVY,
  },
  exploreButtonSub: {
    fontSize: 13,
    color: TEXT_MUTED,
    marginTop: 3,
  },
  signInRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 6,
    marginBottom: 12,
  },
  signInPrompt: {
    fontSize: 15,
    color: TEXT_MUTED,
  },
  signInLink: {
    fontSize: 15,
    fontWeight: '700',
    color: CORAL,
  },
  businessLink: {
    alignItems: 'center',
    paddingVertical: 6,
  },
  businessLinkText: {
    fontSize: 14,
    color: NAVY,
    fontWeight: '600',
    opacity: 0.85,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    padding: 20,
  },
  modalCard: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 22,
    maxWidth: 420,
    width: '100%',
    alignSelf: 'center',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: NAVY,
    marginBottom: 8,
  },
  modalSubtitle: {
    fontSize: 14,
    color: TEXT_MUTED,
    lineHeight: 20,
    marginBottom: 16,
  },
  zipInput: {
    borderWidth: 1,
    borderColor: '#e8e4df',
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 16,
    marginBottom: 8,
  },
  zipError: {
    color: '#dc3545',
    fontSize: 13,
    marginBottom: 8,
  },
  modalPrimary: {
    backgroundColor: CORAL,
    borderRadius: 10,
    paddingVertical: 14,
    alignItems: 'center',
    marginTop: 8,
  },
  modalPrimaryText: {
    color: '#fff',
    fontWeight: '700',
    fontSize: 16,
  },
  modalCancel: {
    paddingVertical: 14,
    alignItems: 'center',
    marginTop: 4,
  },
  modalCancelText: {
    color: TEXT_MUTED,
    fontSize: 15,
    fontWeight: '600',
  },
});

export default GuestWelcomeScreen;
